import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BookOpen, Compass, Users, Building2, Bell } from 'lucide-react';
import { supabase } from '../supabase';
import { calcularEAtribuirBadges } from '../utils/badges';
import CadastrarLivro from './CadastrarLivro';
import DetalheLivro from './DetalheLivro';
import Onboarding from './Onboarding';
import Notificacoes from './Notificacoes';
import PerfilCompleto from './PerfilCompleto';
import Clubes from './Clubes';
import Explorar from './Explorar';
import Amigos from './Amigos';
import { useToast } from '../context/ToastContext';
import { usePlan, LIMITES } from '../context/PlanContext';
import { useRefresh } from '../context/RefreshContext';
import AvatarPremium from '../components/AvatarPremium';
import Logo from '../components/Logo';
import { FRASES } from '../utils/frases';

const BRAND = '#5B3CE4';
const INK = '#211C2B';
const INK400 = '#9A93A4';

function SidebarDesktop({ tela, setTela, notifPendentes, nomeUsuario, fotoPerfil, isPago }) {
  const { openUpgrade } = usePlan();
  const navItems = [
    { id: 'acervo', label: 'Biblioteca', icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 19a9 9 0 0 1 9 0a9 9 0 0 1 9 0"/><path d="M3 6a9 9 0 0 1 9 0a9 9 0 0 1 9 0"/><line x1="3" y1="6" x2="3" y2="19"/><line x1="12" y1="6" x2="12" y2="19"/><line x1="21" y1="6" x2="21" y2="19"/></svg> },
    { id: 'explorar', label: 'Explorar', icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg> },
    { id: 'amigos', label: 'Amigos', icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><polyline points="16 11 18 13 22 9"/></svg> },
    { id: 'clubes', label: 'Clubes', icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
    { id: 'notificacoes', label: 'Notificações', badge: notifPendentes, icon: <Bell size={17} strokeWidth={1.8} /> },
  ];
  return (
    <aside className="hidden lg:flex flex-col flex-shrink-0 border-r border-line-soft bg-paper" style={{ width: 252 }}>
      {/* Logo */}
      <div style={{ padding: '24px 20px 14px' }}>
        <Logo size="md" />
      </div>
      {/* Nav */}
      <nav style={{ padding: '0 12px' }}>
        {navItems.map(item => {
          const ativo = tela === item.id;
          return (
            <button key={item.id} onClick={() => setTela(item.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 12px', borderRadius: 12, marginBottom: 2, background: ativo ? '#EDE9FF' : 'transparent', color: ativo ? BRAND : INK400, border: 'none', cursor: 'pointer', fontWeight: ativo ? 700 : 500, fontSize: 14 }}>
              {item.icon}
              <span style={{ flex: 1, textAlign: 'left' }}>{item.label}</span>
              {item.badge > 0 && <span style={{ background: '#EF4444', color: '#fff', borderRadius: 10, fontSize: 10, fontWeight: 700, padding: '1px 6px', minWidth: 16, textAlign: 'center' }}>{item.badge}</span>}
            </button>
          );
        })}
      </nav>
      {/* Separador + Adicionar livro (pill) */}
      <div style={{ height: 1, background: '#F2EEE9', margin: '14px 8px' }} />
      <div style={{ padding: '0 12px 14px' }}>
        <button onClick={() => setTela('cadastrar')}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, width: '100%', padding: '11px 14px', borderRadius: 999, background: BRAND, color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 15, boxShadow: '0 6px 16px rgba(91,60,228,.32)' }}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>
          Adicionar livro
        </button>
      </div>
      <div style={{ flex: 1 }} />
      {/* Perfil */}
      <div style={{ padding: '12px 16px 20px', borderTop: '1px solid #F2EEE9' }}>
        {!isPago && (
          <button onClick={() => openUpgrade()}
            style={{ display: 'block', width: '100%', marginBottom: 8, padding: '7px 12px', borderRadius: 11, border: 'none', background: '#F0ECFE', color: '#5B3CE4', fontSize: 12, fontWeight: 700, cursor: 'pointer', textAlign: 'center' }}>
            Upgrade para Premium
          </button>
        )}
        <button onClick={() => setTela('perfil')}
          style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}>
          <div style={{ position: 'relative', width: 34, height: 34, flexShrink: 0 }}>
            {fotoPerfil
              ? <img src={fotoPerfil} alt="" style={{ width: 34, height: 34, borderRadius: 10, objectFit: 'cover' }} />
              : <div style={{ width: 34, height: 34, borderRadius: 10, background: '#EDE9FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={BRAND} strokeWidth="2.2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                </div>}
            {!isPago && <span style={{ position: 'absolute', bottom: -5, left: 0, fontSize: 8, fontWeight: 800, color: '#5B3CE4', background: '#EDE9FF', borderRadius: 4, padding: '1px 4px', lineHeight: 1.4, whiteSpace: 'nowrap' }}>Free</span>}
            {isPago && <span style={{ position: 'absolute', bottom: -5, left: 0, fontSize: 8, fontWeight: 800, color: '#B87A00', background: '#FFF8E1', borderRadius: 4, padding: '1px 4px', lineHeight: 1.4, whiteSpace: 'nowrap' }}>Premium</span>}
          </div>
          <span style={{ fontSize: 13, fontWeight: 600, color: INK, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left', flex: 1, minWidth: 0 }}>{nomeUsuario}</span>
        </button>
      </div>
    </aside>
  );
}

function getSaudacao() {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return 'Bom dia';
  if (h >= 12 && h < 18) return 'Boa tarde';
  return 'Boa noite';
}

function tempoAtras(dateStr) {
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60) return 'agora';
  if (diff < 3600) return `há ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `há ${Math.floor(diff / 3600)} h`;
  const d = Math.floor(diff / 86400);
  return d === 1 ? 'ontem' : `há ${d} dias`;
}

function Acervo() {
  const { showToast } = useToast();
  const { isPago, openUpgrade } = usePlan();
  const { refreshKey, triggerRefresh } = useRefresh();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('upgrade') === 'success') {
      showToast('Bem-vindo ao Premium! Obrigado pelo apoio.', 'success');
      navigate('/home', { replace: true });
    }
  }, [searchParams, showToast, navigate]);
  const [livros, setLivros] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [tela, setTela] = useState('acervo');
  const [livroSelecionado, setLivroSelecionado] = useState(null);
  const [nomeUsuario, setNomeUsuario] = useState('');
  const [fotoPerfil, setFotoPerfil] = useState(null);
  const [userSlug, setUserSlug] = useState('');
  const [userId, setUserId] = useState(null);
  const [onboardingVisto, setOnboardingVisto] = useState(true);
  const [notifPendentes, setNotifPendentes] = useState(0);
  const [, setSolicitacoesPendentes] = useState(0);
  const [clubeIdParaAbrir, setClubeIdParaAbrir] = useState(null);
  const [emprestimosAtrasados, setEmprestimosAtrasados] = useState(0);
  const [busca, setBusca] = useState('');
  const [categoria, setCategoria] = useState('Tudo');
  const [clubesDoUsuario, setClubesDoUsuario] = useState([]);
  const [livrosNosClubes, setLivrosNosClubes] = useState([]);
  const [livroParaAdicionar, setLivroParaAdicionar] = useState(null);
  const [adicionandoAoClube, setAdicionandoAoClube] = useState(null);
  const [mostrarTourHome, setMostrarTourHome] = useState(false);
  const [emprestimosAtivos, setEmprestimosAtivos] = useState([]);
  const [emprestimoDetalhe, setEmprestimoDetalhe] = useState(null);

  const [cancelando, setCancelando] = useState(false);
  const [modalCancelamento, setModalCancelamento] = useState(null);
  const [cancelJustificativa, setCancelJustificativa] = useState('');
  const [aprovandoEmp, setAprovandoEmp] = useState(false);
  const [meuWhatsapp, setMeuWhatsapp] = useState(null);
  const [salvandoEntrega, setSalvandoEntrega] = useState(false);
  const [salvandoDevolucao, setSalvandoDevolucao] = useState(false);
  const [sugestoesLeitura, setSugestoesLeitura] = useState([]);
  const [atividadeAmigos, setAtividadeAmigos] = useState([]);
  const [livroDetalhe, setLivroDetalhe] = useState(null);
  const [modalAvaliacaoPos, setModalAvaliacaoPos] = useState(null);
  const [avaliacaoEstrelas, setAvaliacaoEstrelas] = useState(0);
  const [avaliacaoComentario, setAvaliacaoComentario] = useState('');
  const [salvandoAvaliacao, setSalvandoAvaliacao] = useState(false);
  const [ordenacao, setOrdenacao] = useState('az');
  const [visualizacao, setVisualizacao] = useState(() => localStorage.getItem('acervo_visualizacao') || 'lista');
  const [menuOrdem, setMenuOrdem] = useState(false);
  const [abaBiblioteca, setAbaBiblioteca] = useState('livros');

  // Fluxo B — registrar empréstimo direto
  const [modalRegistrarAberto, setModalRegistrarAberto] = useState(false);
  const [livroParaRegistrar, setLivroParaRegistrar] = useState(null);
  const [buscaRegistrar, setBuscaRegistrar] = useState('');
  const [usuariosBuscados, setUsuariosBuscados] = useState([]);
  const [buscandoRegistrar, setBuscandoRegistrar] = useState(false);
  const [leitorRegistrar, setLeitorRegistrar] = useState(null);
  const [expandirExterno, setExpandirExterno] = useState(false);
  const [nomeExterno, setNomeExterno] = useState('');
  const [wppExterno, setWppExterno] = useState('');
  const [prazoRegistrar, setPrazoRegistrar] = useState(14);
  const [registrando, setRegistrando] = useState(false);
  const timerBuscaRegistrar = useRef(null);

  const saudacao = getSaudacao();
  const fraseDoDia = FRASES[Math.floor(Date.now() / 86400000) % FRASES.length];

  useEffect(() => {
    async function carregarUsuario() {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const nome = user.user_metadata?.nome || user.email.split('@')[0];
        setNomeUsuario(nome);
        const { data: perfil } = await supabase
          .from('perfis').select('slug, username, foto_url, plano').eq('id', user.id).maybeSingle();
        const { data: wppPerfil } = await supabase
          .from('perfis').select('whatsapp').eq('id', user.id).maybeSingle();
        setMeuWhatsapp(wppPerfil?.whatsapp || null);
        setUserSlug(perfil?.username ? `@${perfil.username}` : (perfil?.slug || nome.toLowerCase().replace(/\s+/g, '-')));
        setFotoPerfil(perfil?.foto_url || user.user_metadata?.avatar_url || null);
        const visto = localStorage.getItem(`onboarding_${user.id}`);
        setOnboardingVisto(!!visto);
        const { data: notifs } = await supabase
          .from('notificacoes').select('id').eq('user_id', user.id).eq('lida', false);
        setNotifPendentes(notifs?.length || 0);
        // setUserId por último: dispara buscarLivros, que precisa de onboardingVisto já definido
        setUserId(user.id);
      }
    }
    carregarUsuario();
  }, []);

  useEffect(() => {
    if (!userId) return;
    if (localStorage.getItem(`tour_home_${userId}`) === 'needed') {
      setMostrarTourHome(true);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    const intervalo = setInterval(async () => {
      const { data: notifs } = await supabase
        .from('notificacoes').select('id').eq('user_id', userId).eq('lida', false);
      setNotifPendentes(notifs?.length || 0);
    }, 30000);
    return () => clearInterval(intervalo);
  }, [userId]);

  const buscarLivros = useCallback(async () => {
    if (!userId) return;
    setCarregando(true);
    const { data, error } = await supabase.from('livros').select('*').eq('user_id', userId);
    if (!error) setLivros(data);
    setCarregando(false);
  }, [userId]);

  useEffect(() => { buscarLivros(); }, [buscarLivros]);

  useEffect(() => {
    if (!userId) return;
    async function carregarEmprestimosAtivos() {
      const ATIVOS = ['pendente', 'aprovado', 'em_andamento'];
      const DIAS_EXPIRACAO = 3;

      const [{ data: comoLeitor }, { data: meusLivros }] = await Promise.all([
        supabase
          .from('emprestimos')
          .select('id, status, created_at, prazo_dias, data_devolucao, entrega_confirmada_dono, entrega_confirmada_leitor, devolucao_confirmada_dono, devolucao_confirmada_leitor, livros(id, titulo, autor, capa_url, user_id)')
          .eq('leitor_user_id', userId)
          .in('status', ATIVOS),
        supabase.from('livros').select('id').eq('user_id', userId),
      ]);

      const livroIds = (meusLivros || []).map(l => l.id);
      let comoDono = [];
      if (livroIds.length > 0) {
        const { data } = await supabase
          .from('emprestimos')
          .select('id, status, created_at, prazo_dias, data_devolucao, entrega_confirmada_dono, entrega_confirmada_leitor, devolucao_confirmada_dono, devolucao_confirmada_leitor, leitor_user_id, leitor_nome, livros(id, titulo, autor, capa_url)')
          .in('livro_id', livroIds)
          .in('status', ATIVOS);
        comoDono = data || [];
      }

      // Auto-expirar pedidos pendentes sem resposta após 3 dias
      const agora = Date.now();
      const expirados = [
        ...(comoLeitor || []).filter(e => e.status === 'pendente' && (agora - new Date(e.created_at).getTime()) > DIAS_EXPIRACAO * 86400000),
        ...comoDono.filter(e => e.status === 'pendente' && (agora - new Date(e.created_at).getTime()) > DIAS_EXPIRACAO * 86400000),
      ];
      if (expirados.length > 0) {
        const idsExpirados = [...new Set(expirados.map(e => e.id))];
        await supabase.from('emprestimos').update({ status: 'expirado' }).in('id', idsExpirados);
        // Notificar ambas as partes
        const notifs = expirados.flatMap(e => {
          const titulo = e.livros?.titulo || 'Livro';
          const notif = (uid) => uid ? { user_id: uid, emprestimo_id: e.id, tipo: 'solicitacao_expirada', lida: false, meta: { titulo } } : null;
          return [notif(e.leitor_user_id || userId), notif(e.livros?.user_id || null)].filter(Boolean);
        });
        if (notifs.length > 0) await supabase.from('notificacoes').insert(notifs);
      }
      const idsExpiradosSet = new Set(expirados.map(e => e.id));

      const donoIds = (comoLeitor || []).map(e => e.livros?.user_id).filter(Boolean);
      const leitorIds = comoDono.map(e => e.leitor_user_id).filter(Boolean);
      const allIds = [...new Set([...donoIds, ...leitorIds])];
      let perfisMap = {};
      if (allIds.length > 0) {
        const { data: perfis } = await supabase.from('perfis').select('id, nome').in('id', allIds);
        const { data: wppPerfis } = await supabase.from('perfis').select('id, whatsapp').in('id', allIds);
        const wppMap = Object.fromEntries((wppPerfis || []).map(p => [p.id, p.whatsapp]));
        (perfis || []).forEach(p => { perfisMap[p.id] = { ...p, whatsapp: wppMap[p.id] || null }; });
      }

      const leitorItems = (comoLeitor || []).filter(e => !idsExpiradosSet.has(e.id)).map(e => ({
        ...e, role: 'leitor',
        outraPessoa: perfisMap[e.livros?.user_id] || { nome: 'Anfitrião' },
      }));
      const donoItems = comoDono.filter(e => !idsExpiradosSet.has(e.id)).map(e => ({
        ...e, role: 'dono',
        outraPessoa: perfisMap[e.leitor_user_id] || { nome: e.leitor_nome || 'Leitor' },
      }));

      const stepOrder = { pendente: 0, aprovado: 1, ativo: 2, em_andamento: 2 };
      setEmprestimosAtivos(
        [...leitorItems, ...donoItems].sort((a, b) => (stepOrder[a.status] ?? 3) - (stepOrder[b.status] ?? 3))
      );
    }
    carregarEmprestimosAtivos();
  }, [userId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!userId) return;
    async function carregarClubes() {
      const [{ data: membros }, { data: clLivros }] = await Promise.all([
        supabase.from('clube_membros').select('clube_id, clubes(id, nome)').eq('user_id', userId).eq('status', 'aprovado'),
        supabase.from('clube_livros').select('clube_id, livro_id').eq('user_id', userId),
      ]);
      setClubesDoUsuario(membros || []);
      setLivrosNosClubes(clLivros || []);
    }
    carregarClubes();
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    async function buscarSugestoes() {
      const { data: seguindoData } = await supabase
        .from('seguidores').select('seguido_id').eq('seguidor_id', userId);
      const seguindoIds = (seguindoData || []).map(r => r.seguido_id);
      if (seguindoIds.length === 0) return;

      // Atividade recente dos amigos (para o rail do desktop)
      const { data: recentes } = await supabase
        .from('livros').select('id, titulo, autor, user_id, created_at')
        .in('user_id', seguindoIds).order('created_at', { ascending: false }).limit(6);
      if (recentes?.length) {
        const donoIdsR = [...new Set(recentes.map(l => l.user_id))];
        const { data: perfsR } = await supabase.from('perfis').select('id, nome, foto_url').in('id', donoIdsR);
        const perfsRMap = {};
        (perfsR || []).forEach(p => { perfsRMap[p.id] = p; });
        setAtividadeAmigos(recentes.map(l => ({ ...l, perfil: perfsRMap[l.user_id] || null })).filter(l => l.perfil));
      }

      const [{ data: meusLivros }, { data: livrosAmigos }] = await Promise.all([
        supabase.from('livros').select('titulo, autor, estrelas').eq('user_id', userId),
        supabase.from('livros').select('id, titulo, autor, capa_url, descricao, estrelas, user_id')
          .in('user_id', seguindoIds).eq('disponivel', true).neq('especial', true),
      ]);

      if (!livrosAmigos?.length) return;

      const likedAuthors = new Set(
        (meusLivros || []).filter(l => (l.estrelas || 0) >= 4).map(l => l.autor).filter(Boolean)
      );
      const meusTitulos = new Set(
        (meusLivros || []).map(l => l.titulo?.toLowerCase().trim()).filter(Boolean)
      );

      const donoIds = [...new Set(livrosAmigos.map(l => l.user_id))];
      const { data: perfisAmigos } = await supabase
        .from('perfis').select('id, nome, slug, foto_url, plano').in('id', donoIds);
      const perfisMap = {};
      (perfisAmigos || []).forEach(p => { perfisMap[p.id] = p; });

      const scored = livrosAmigos
        .filter(l => !meusTitulos.has(l.titulo?.toLowerCase().trim()))
        .map(l => ({ ...l, perfil: perfisMap[l.user_id] || null, score: (likedAuthors.has(l.autor) ? 3 : 0) + (l.estrelas || 0) }))
        .filter(l => l.perfil)
        .sort((a, b) => b.score - a.score)
        .slice(0, 8);

      setSugestoesLeitura(scored);
    }
    buscarSugestoes();
  }, [userId]);

  async function handleSalvarAvaliacaoPos(pular = false) {
    if (!modalAvaliacaoPos) return;
    if (!pular && avaliacaoEstrelas > 0) {
      setSalvandoAvaliacao(true);
      await supabase.from('emprestimos').update({
        leitor_feedback_estrelas: avaliacaoEstrelas,
        leitor_feedback_comentario: avaliacaoComentario || null,
      }).eq('id', modalAvaliacaoPos.id);
      setSalvandoAvaliacao(false);
      showToast('Avaliação salva! Obrigado.', 'success');
    } else if (pular) {
      showToast('Dono notificado! Avalie depois nas notificações.', 'info');
    }
    setModalAvaliacaoPos(null);
  }

  async function handleAprovarInline(emp) {
    if (!meuWhatsapp) {
      showToast('Adicione seu WhatsApp no perfil antes de aprovar empréstimos.', 'error');
      return;
    }
    setAprovandoEmp(true);
    await supabase.from('emprestimos').update({ status: 'aprovado' }).eq('id', emp.id);
    await supabase.from('livros').update({ disponivel: false }).eq('id', emp.livros.id);
    if (emp.leitor_user_id) {
      const { data: leitorPerfil } = await supabase.from('perfis').select('whatsapp').eq('id', emp.leitor_user_id).maybeSingle();
      await supabase.from('notificacoes').insert({
        user_id: emp.leitor_user_id, emprestimo_id: emp.id, tipo: 'emprestimo_aprovado', lida: false,
        meta: { dono_whatsapp: meuWhatsapp || null, leitor_whatsapp: leitorPerfil?.whatsapp || null, livro_titulo: emp.livros?.titulo },
      });
    }
    setEmprestimosAtivos(prev => prev.map(e => e.id === emp.id ? { ...e, status: 'aprovado' } : e));
    triggerRefresh(); setAprovandoEmp(false);
    setEmprestimoDetalhe(null);
    showToast('Pedido aprovado! O leitor ja pode entrar em contato.', 'success');
  }

  async function handleRecusarInline(emp) {
    setAprovandoEmp(true);
    await supabase.from('emprestimos').update({ status: 'recusado' }).eq('id', emp.id);
    if (emp.leitor_user_id) {
      await supabase.from('notificacoes').insert({
        user_id: emp.leitor_user_id, emprestimo_id: emp.id, tipo: 'emprestimo_recusado', lida: false,
      });
    }
    setEmprestimosAtivos(prev => prev.filter(e => e.id !== emp.id));
    triggerRefresh(); setAprovandoEmp(false);
    setEmprestimoDetalhe(null);
    showToast('Pedido recusado.', 'info');
  }

  async function handleConfirmarEntregaDono(emp) {
    setSalvandoEntrega(true);
    const indeterminado = emp.prazo_dias === 0;
    let update = { status: 'em_andamento' };
    let data_devolucao = null;
    if (!indeterminado) {
      const prazo = emp.prazo_dias || 14;
      const dataD = new Date();
      dataD.setDate(dataD.getDate() + prazo);
      data_devolucao = dataD.toISOString().split('T')[0];
      update.data_devolucao = data_devolucao;
    }
    await supabase.from('emprestimos').update(update).eq('id', emp.id);
    if (emp.leitor_user_id) {
      await supabase.from('notificacoes').insert({
        user_id: emp.leitor_user_id, emprestimo_id: emp.id, tipo: 'livro_entregue', lida: false,
        meta: { livro_titulo: emp.livros?.titulo, ...(data_devolucao ? { data_devolucao } : {}) },
      });
    }
    setEmprestimosAtivos(prev => prev.map(e => e.id === emp.id
      ? { ...e, status: 'em_andamento', data_devolucao } : e));
    showToast('Entrega confirmada! Emprestimo iniciado.', 'success');
    triggerRefresh(); setSalvandoEntrega(false); setEmprestimoDetalhe(null);
  }

  async function handleSolicitarDevolucao(emp) {
    setSalvandoDevolucao(true);
    const dataD = new Date();
    dataD.setDate(dataD.getDate() + 7);
    const data_devolucao = dataD.toISOString().split('T')[0];
    await supabase.from('emprestimos').update({ data_devolucao }).eq('id', emp.id);
    if (emp.leitor_user_id) {
      await supabase.from('notificacoes').insert({
        user_id: emp.leitor_user_id, emprestimo_id: emp.id, tipo: 'solicitacao_devolucao', lida: false,
        meta: { livro_titulo: emp.livros?.titulo, data_devolucao },
      });
    }
    setEmprestimosAtivos(prev => prev.map(e => e.id === emp.id ? { ...e, data_devolucao } : e));
    showToast('Devolução solicitada. O leitor tem 7 dias.', 'success');
    setSalvandoDevolucao(false); setEmprestimoDetalhe(null);
  }

  async function handleConfirmarDevolucaoLeitor(emp) {
    setSalvandoDevolucao(true);
    const { data: atual } = await supabase.from('emprestimos').select('devolucao_confirmada_dono').eq('id', emp.id).maybeSingle();
    await supabase.from('emprestimos').update({ devolucao_confirmada_leitor: true }).eq('id', emp.id);
    if (atual?.devolucao_confirmada_dono) {
      await Promise.all([
        supabase.from('emprestimos').update({ status: 'devolvido' }).eq('id', emp.id),
        supabase.from('livros').update({ disponivel: true }).eq('id', emp.livros.id),
      ]);
      const donoId = emp.livros?.user_id;
      if (donoId) {
        await supabase.from('notificacoes').insert({ user_id: donoId, emprestimo_id: emp.id, tipo: 'avaliar_leitor', lida: false });
      }
      if (userId) {
        await supabase.from('notificacoes').insert({ user_id: userId, emprestimo_id: emp.id, tipo: 'avaliar_livro', lida: false });
      }
      if (userId) calcularEAtribuirBadges(userId);
      setEmprestimosAtivos(prev => prev.filter(e => e.id !== emp.id));
      showToast('Devolucao confirmada! Obrigado.', 'success');
    } else {
      const donoId = emp.livros?.user_id;
      if (donoId) {
        await supabase.from('notificacoes').insert({
          user_id: donoId, emprestimo_id: emp.id, tipo: 'leitor_diz_devolveu', lida: false,
          meta: { leitor_nome: nomeUsuario, livro_titulo: emp.livros?.titulo },
        });
      }
      setEmprestimosAtivos(prev => prev.map(e => e.id === emp.id ? { ...e, devolucao_confirmada_leitor: true } : e));
      showToast('Devolucao registrada. Aguardando o dono confirmar.', 'success');
    }
    triggerRefresh(); setSalvandoDevolucao(false); setEmprestimoDetalhe(null);
  }

  async function handleConfirmarDevolucaoDono(emp) {
    setSalvandoDevolucao(true);
    await Promise.all([
      supabase.from('emprestimos').update({ devolucao_confirmada_dono: true, status: 'devolvido' }).eq('id', emp.id),
      supabase.from('livros').update({ disponivel: true }).eq('id', emp.livros.id),
    ]);
    if (emp.leitor_user_id) {
      await supabase.from('notificacoes').insert({ user_id: emp.leitor_user_id, emprestimo_id: emp.id, tipo: 'avaliar_livro', lida: false });
      calcularEAtribuirBadges(emp.leitor_user_id);
    }
    if (userId) {
      await supabase.from('notificacoes').insert({ user_id: userId, emprestimo_id: emp.id, tipo: 'avaliar_leitor', lida: false });
    }
    setEmprestimosAtivos(prev => prev.filter(e => e.id !== emp.id));
    showToast('Devolucao confirmada!', 'success');
    triggerRefresh(); setSalvandoDevolucao(false); setEmprestimoDetalhe(null);
  }

  async function handleReportarInadimplente(emp) {
    if (!emp.leitor_user_id) return;
    setSalvandoDevolucao(true);
    await supabase.from('perfis').update({ emprestimo_bloqueado: true }).eq('id', emp.leitor_user_id);
    await supabase.from('notificacoes').insert([
      {
        user_id: userId,
        emprestimo_id: emp.id,
        tipo: 'emprestimo_inadimplente',
        lida: false,
        meta: { papel: 'dono', leitor_user_id: emp.leitor_user_id, leitor_nome: emp.leitor_nome, livro_titulo: emp.livros?.titulo },
      },
      {
        user_id: emp.leitor_user_id,
        emprestimo_id: emp.id,
        tipo: 'emprestimo_inadimplente',
        lida: false,
        meta: { papel: 'leitor', livro_titulo: emp.livros?.titulo },
      },
    ]);
    showToast('Leitor marcado como inadimplente. Conta bloqueada ate devolucao.', 'success');
    setSalvandoDevolucao(false);
    setEmprestimoDetalhe(null);
    triggerRefresh();
  }

  function handleCancelar(emp) {
    setCancelJustificativa('');
    setModalCancelamento(emp);
  }

  async function handleCancelarConfirmar() {
    const emp = modalCancelamento;
    if (!emp) return;
    setCancelando(true);
    const canceladoPor = emp.role;
    await supabase.from('emprestimos').update({
      status: 'cancelado',
      cancelado_por: canceladoPor,
      cancelamento_justificativa: cancelJustificativa.trim() || null,
    }).eq('id', emp.id);
    if (emp.status === 'aprovado' && emp.livros?.id) {
      await supabase.from('livros').update({ disponivel: true }).eq('id', emp.livros.id);
    }
    const outroId = emp.role === 'leitor' ? emp.livros?.user_id : emp.leitor_user_id;
    if (outroId) {
      await supabase.from('notificacoes')
        .update({ lida: true }).eq('emprestimo_id', emp.id).eq('user_id', outroId).eq('lida', false);
      await supabase.from('notificacoes').insert({
        user_id: outroId, emprestimo_id: emp.id, tipo: 'emprestimo_cancelado', lida: false,
        meta: { livro_titulo: emp.livros?.titulo, cancelado_por: canceladoPor, justificativa: cancelJustificativa.trim() || null },
      });
    }
    setEmprestimosAtivos(prev => prev.filter(e => e.id !== emp.id));
    triggerRefresh(); setCancelando(false);
    setModalCancelamento(null);
    setEmprestimoDetalhe(null);
    showToast('Empréstimo cancelado.', 'success');
  }

  useEffect(() => {
    clearTimeout(timerBuscaRegistrar.current);
    if (!modalRegistrarAberto || expandirExterno || leitorRegistrar) return;
    const q = buscaRegistrar.replace(/^@/, '').trim();
    if (q.length < 2) { setUsuariosBuscados([]); return; }
    setBuscandoRegistrar(true);
    timerBuscaRegistrar.current = setTimeout(async () => {
      const { data } = await supabase
        .from('perfis').select('id, nome, username, foto_url')
        .neq('id', userId)
        .or(`nome.ilike.%${q}%,username.ilike.%${q}%`)
        .limit(10);
      setUsuariosBuscados(data || []);
      setBuscandoRegistrar(false);
    }, 350);
  }, [buscaRegistrar, modalRegistrarAberto, expandirExterno, leitorRegistrar, userId]);

  async function handleRegistrarEmprestimo() {
    if (!livroParaRegistrar) return;
    if (!leitorRegistrar && !nomeExterno.trim()) return;
    setRegistrando(true);
    const empData = {
      livro_id: livroParaRegistrar.id,
      status: 'em_andamento',
      prazo_dias: prazoRegistrar,
    };
    if (prazoRegistrar > 0) {
      const dataD = new Date();
      dataD.setDate(dataD.getDate() + prazoRegistrar);
      empData.data_devolucao = dataD.toISOString().split('T')[0];
    }
    if (leitorRegistrar) {
      empData.leitor_user_id = leitorRegistrar.id;
      empData.leitor_nome = leitorRegistrar.nome;
    } else {
      empData.leitor_nome = nomeExterno.trim();
    }
    const { data: novoEmp } = await supabase.from('emprestimos').insert(empData).select('id').maybeSingle();
    await supabase.from('livros').update({ disponivel: false }).eq('id', livroParaRegistrar.id);
    if (leitorRegistrar?.id && novoEmp) {
      await supabase.from('notificacoes').insert({
        user_id: leitorRegistrar.id, emprestimo_id: novoEmp.id,
        tipo: 'emprestimo_registrado_dono', lida: false,
        meta: { livro_titulo: livroParaRegistrar.titulo, dono_nome: nomeUsuario },
      });
      showToast('Emprestimo registrado! Leitor foi notificado.', 'success');
    } else if (novoEmp) {
      const wpp = wppExterno.replace(/\D/g, '');
      const link = `https://bibliotecadebolso.app/convite/emprestimo/${novoEmp.id}`;
      const msg = `Oi ${nomeExterno.trim()}! ${nomeUsuario} registrou que te emprestou "${livroParaRegistrar.titulo}" no Biblioteca de Bolso. Clique para confirmar: ${link}`;
      if (wpp.length >= 10) {
        window.open(`https://wa.me/55${wpp}?text=${encodeURIComponent(msg)}`, '_blank');
      }
      showToast('Emprestimo registrado!', 'success');
    }
    triggerRefresh();
    setModalRegistrarAberto(false);
    setBuscaRegistrar(''); setLeitorRegistrar(null); setNomeExterno(''); setWppExterno('');
    setExpandirExterno(false); setPrazoRegistrar(14); setRegistrando(false);
  }

  async function handleAdicionarAoClube(livro, clubeId) {
    setAdicionandoAoClube(clubeId);
    const { error } = await supabase.from('clube_livros').insert({
      clube_id: clubeId,
      livro_id: livro.id,
      user_id: userId,
    });
    setAdicionandoAoClube(null);
    if (error?.code === '23505') {
      showToast('Livro já está nesse clube.', 'info');
    } else if (error) {
      showToast('Erro ao adicionar ao clube.', 'error');
    } else {
      setLivrosNosClubes(prev => [...prev, { clube_id: clubeId, livro_id: livro.id }]);
      showToast('Livro adicionado ao clube!', 'success');
    }
    setLivroParaAdicionar(null);
  }

  useEffect(() => {
    if (livros.length === 0) return;
    async function buscarContexto() {
      const livroIds = livros.map(l => l.id);
      const hoje = new Date().toISOString().split('T')[0];
      const [{ data: pend }, { data: atr }] = await Promise.all([
        supabase.from('emprestimos').select('id').in('livro_id', livroIds).eq('status', 'pendente'),
        supabase.from('emprestimos').select('id').in('livro_id', livroIds).eq('status', 'aprovado').lt('data_devolucao', hoje),
      ]);
      setSolicitacoesPendentes(pend?.length || 0);
      setEmprestimosAtrasados(atr?.length || 0);
    }
    buscarContexto();
  }, [livros]);

  function concluirOnboarding(irParaCadastro = false) {
    localStorage.setItem(`onboarding_${userId}`, 'done');
    localStorage.setItem(`tour_home_${userId}`, 'needed');
    setOnboardingVisto(true);
    if (irParaCadastro) setTela('cadastrar');
    else navigate('/perfil');
  }

  const livrosOrdenados = [...livros].sort((a, b) => {
    if (ordenacao === 'estrelas') return (b.estrelas || 0) - (a.estrelas || 0);
    if (ordenacao === 'disponivel') return (b.disponivel ? 1 : 0) - (a.disponivel ? 1 : 0);
    return a.titulo.localeCompare(b.titulo);
  });

  const livrosFiltradosCat = livrosOrdenados.filter(l => {
    if (categoria === 'Disponíveis' && (!l.disponivel || l.especial)) return false;
    if (categoria === 'Emprestados' && l.disponivel) return false;
    if (categoria === 'Especiais' && !l.especial) return false;
    if (!['Tudo', 'Disponíveis', 'Emprestados', 'Especiais'].includes(categoria) && l.genero !== categoria) return false;
    if (busca.trim()) {
      const q = busca.toLowerCase();
      return l.titulo?.toLowerCase().includes(q) || l.autor?.toLowerCase().includes(q);
    }
    return true;
  });

  // Map livro.id → active loan (dono view only) for day counter on cards
  const emprestimoAtivoPorLivro = {};
  emprestimosAtivos
    .filter(e => e.role === 'dono' && (e.status === 'em_andamento' || e.status === 'aprovado'))
    .forEach(e => { if (e.livros?.id) emprestimoAtivoPorLivro[e.livros.id] = e; });

  function infoPrazoBadge(livroId) {
    const emp = emprestimoAtivoPorLivro[livroId];
    if (!emp?.data_devolucao) return null;
    const dias = Math.ceil((new Date(emp.data_devolucao + 'T12:00:00') - new Date()) / 86400000);
    if (dias < 0)  return { label: `${Math.abs(dias)}d atr.`, bg: '#FDECEA', color: '#C62828', dot: '#E53935' };
    if (dias === 0) return { label: 'hoje',               bg: '#FFF3E0', color: '#E65100', dot: '#EF6C00' };
    if (dias <= 3)  return { label: `${dias}d`,           bg: '#FFF3E0', color: '#BF6500', dot: '#EF8C00' };
    return              { label: `${dias}d`,              bg: '#FBF1DC', color: '#9A6B12', dot: '#E0A02E' };
  }

  if (!onboardingVisto && !carregando && livros.length === 0) return <Onboarding onConcluir={concluirOnboarding} onPular={() => concluirOnboarding(false)} userId={userId} nomeUsuario={nomeUsuario} />;

  const sp = { tela, setTela, notifPendentes, nomeUsuario, fotoPerfil, isPago };

  function withSidebar(content) {
    return (
      <div className="lg:flex bg-paper" style={{ height: '100dvh', overflow: 'hidden' }}>
        <SidebarDesktop {...sp} />
        <div style={{ flex: 1, overflow: 'hidden' }}>{content}</div>
      </div>
    );
  }

  if (tela === 'cadastrar') return withSidebar(<CadastrarLivro onVoltar={() => setTela('acervo')} onSalvo={() => { setTela('acervo'); buscarLivros(); }} />);
  if (tela === 'clubes') return withSidebar(<Clubes onVoltar={() => { setTela('acervo'); setClubeIdParaAbrir(null); }} clubeIdParaAbrir={clubeIdParaAbrir} setTelaApp={setTela} />);
  if (tela === 'perfil') return withSidebar(<PerfilCompleto onVoltar={() => setTela('acervo')} />);
  if (tela === 'notificacoes') return withSidebar(<Notificacoes onVoltar={() => { setTela('acervo'); setNotifPendentes(0); }} onIrParaClubes={(id) => { setClubeIdParaAbrir(id || null); setTela('clubes'); }} />);
  if (tela === 'explorar') return withSidebar(<Explorar userId={userId} setTela={setTela} nomeUsuario={nomeUsuario} />);
  if (tela === 'amigos') return withSidebar(<Amigos userId={userId} setTela={setTela} />);
  if (tela === 'detalhe' && livroSelecionado) {
    return withSidebar(
      <DetalheLivro
        livro={livroSelecionado}
        onVoltar={() => { setTela('acervo'); setLivroSelecionado(null); }}
        onExcluido={() => { setTela('acervo'); setLivroSelecionado(null); buscarLivros(); }}
        onRegistrarEmprestimo={(livro) => {
          setLivroParaRegistrar(livro);
          setModalRegistrarAberto(true);
          setTela('acervo');
          setLivroSelecionado(null);
        }}
      />
    );
  }

  const cardShadow = '0 1px 2px rgba(33,28,43,0.04),0 4px 16px rgba(33,28,43,0.05)';

  const emprestimosComPrazo = emprestimosAtivos
    .filter(e => e.data_devolucao && e.status === 'em_andamento')
    .sort((a, b) => new Date(a.data_devolucao) - new Date(b.data_devolucao))
    .slice(0, 5);

  return (
    <div className="bg-paper font-sans" style={{height:'100dvh', overflowY:'auto'}}>

      {/* Modal: Cancelamento */}
      {modalCancelamento && (
        <div className="fixed inset-0 flex items-end lg:items-center justify-center z-50 px-4 pb-6 lg:pb-0"
          style={{background:'rgba(33,28,43,0.55)'}} onClick={() => setModalCancelamento(null)}>
          <div className="bg-white rounded-[28px] p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <div className="lg:hidden w-10 h-1 rounded-full bg-line mx-auto mb-5" />
            <p className="text-[17px] font-extrabold text-ink-900 mb-1">
              {modalCancelamento.status === 'pendente' ? 'Cancelar pedido?' : 'Cancelar empréstimo?'}
            </p>
            <p className="text-[13px] text-ink-400 mb-4">
              {modalCancelamento.status === 'pendente'
                ? 'O pedido será encerrado e o dono será avisado.'
                : 'O empréstimo será encerrado. Ambas as partes serão avisadas.'}
            </p>
            <label className="text-xs font-bold text-ink-400 mb-1.5 block">Justificativa <span className="font-normal">(opcional)</span></label>
            <textarea
              value={cancelJustificativa}
              onChange={e => setCancelJustificativa(e.target.value)}
              placeholder="Explique o motivo do cancelamento..."
              rows={3}
              className="w-full border border-line-soft rounded-xl px-3 py-2.5 text-sm text-ink-900 bg-paper resize-none outline-none focus:border-line mb-4"
            />
            <div className="flex gap-2">
              <button onClick={() => setModalCancelamento(null)}
                className="flex-1 py-3 rounded-[13px] text-sm font-semibold border border-line bg-paper-2 text-ink-700 cursor-pointer">
                Voltar
              </button>
              <button onClick={handleCancelarConfirmar} disabled={cancelando}
                className="flex-1 py-3 rounded-[13px] text-sm font-bold cursor-pointer border-0"
                style={{background:'#C5462A', color:'#fff', opacity: cancelando ? 0.6 : 1}}>
                {cancelando ? 'Cancelando...' : 'Confirmar cancelamento'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Avaliação pós-devolução */}
      {modalAvaliacaoPos && (
        <div className="fixed inset-0 flex items-end lg:items-center justify-center z-50 px-4 pb-6 lg:pb-0"
          style={{background:'rgba(33,28,43,0.55)'}}>
          <div className="bg-white rounded-[28px] p-6 w-full max-w-sm">
            <div className="lg:hidden w-10 h-1 rounded-full bg-line mx-auto mb-5" />
            <div className="text-center mb-5">
              <p className="text-[22px] mb-2">★</p>
              <p className="text-[15px] font-extrabold text-ink-900 mb-1">Como foi o livro?</p>
              <p className="text-xs text-ink-400 truncate">{modalAvaliacaoPos.livros?.titulo}</p>
            </div>
            <div className="flex justify-center gap-3 mb-5">
              {[1,2,3,4,5].map(s => (
                <button key={s} onClick={() => setAvaliacaoEstrelas(s)}
                  className="text-[34px] transition-transform active:scale-125"
                  style={{color: s <= avaliacaoEstrelas ? '#E0A02E' : '#D4CFE2', lineHeight: 1}}>★</button>
              ))}
            </div>
            {avaliacaoEstrelas > 0 && (
              <textarea
                value={avaliacaoComentario}
                onChange={e => setAvaliacaoComentario(e.target.value)}
                placeholder="Comentário opcional..."
                rows={2}
                className="w-full border border-line-soft rounded-[13px] px-4 py-3 text-sm bg-paper outline-none focus:border-line resize-none mb-4" />
            )}
            <button
              onClick={() => handleSalvarAvaliacaoPos(false)}
              disabled={avaliacaoEstrelas === 0 || salvandoAvaliacao}
              className="w-full py-3 rounded-[14px] text-sm font-bold mb-2 transition-opacity disabled:opacity-40"
              style={{background: avaliacaoEstrelas > 0 ? '#5B3CE4' : '#E8E4F4', color: avaliacaoEstrelas > 0 ? 'white' : '#9A93A4'}}>
              {salvandoAvaliacao ? 'Salvando...' : 'Avaliar'}
            </button>
            <button onClick={() => handleSalvarAvaliacaoPos(true)}
              className="w-full py-2.5 text-sm font-semibold text-ink-400">
              Agora não
            </button>
          </div>
        </div>
      )}

      {/* Modal: Adicionar ao clube */}
      {livroParaAdicionar && (
        <div className="fixed inset-0 flex items-end lg:items-center justify-center z-50 px-4 pb-6 lg:pb-0"
          style={{background:'rgba(33,28,43,0.42)'}}
          onClick={() => setLivroParaAdicionar(null)}>
          <div className="bg-white rounded-[28px] p-5 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <div className="w-10 h-1 rounded-full bg-line mx-auto mb-4" />
            <p className="text-[15px] font-extrabold text-ink-900 mb-0.5">Adicionar ao clube</p>
            <p className="text-xs text-ink-400 mb-4 truncate">{livroParaAdicionar.titulo}</p>
            <div className="flex flex-col gap-2">
              {clubesDoUsuario.map(entrada => {
                const clube = entrada.clubes;
                if (!clube) return null;
                const jaEsta = livrosNosClubes.some(
                  cl => cl.clube_id === entrada.clube_id && cl.livro_id === livroParaAdicionar.id
                );
                return (
                  <button key={entrada.clube_id}
                    onClick={() => !jaEsta && handleAdicionarAoClube(livroParaAdicionar, entrada.clube_id)}
                    disabled={jaEsta || adicionandoAoClube === entrada.clube_id}
                    className={`w-full text-left px-4 py-3 rounded-[13px] text-sm border transition-colors flex items-center justify-between font-semibold ${jaEsta ? 'border-line bg-paper text-ink-400 cursor-default' : 'border-line text-ink-900 hover:bg-paper-2'} disabled:opacity-60`}>
                    <span>{clube.nome}</span>
                    {jaEsta ? <span className="text-xs text-ink-300 font-medium">Já adicionado</span>
                      : adicionandoAoClube === entrada.clube_id ? <span className="text-xs text-ink-400">Adicionando…</span>
                      : null}
                  </button>
                );
              })}
            </div>
            <button onClick={() => setLivroParaAdicionar(null)}
              className="w-full mt-3 py-3 text-sm border border-line text-ink-500 font-semibold rounded-[14px] bg-paper-2">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Modal: Detalhe do livro (Próxima leitura) */}
      {livroDetalhe && (
        <div className="fixed inset-0 flex items-end lg:items-center justify-center z-50 px-4 pb-6 lg:pb-0"
          style={{background:'rgba(33,28,43,0.42)'}} onClick={() => setLivroDetalhe(null)}>
          <div className="bg-white rounded-[28px] p-5 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <div className="lg:hidden w-10 h-1 rounded-full bg-line mx-auto mb-5" />
            <div className="flex gap-4 mb-4">
              <div className="w-[64px] h-[96px] rounded-[10px] overflow-hidden bg-paper-2 flex items-center justify-center flex-shrink-0">
                {livroDetalhe.capa_url
                  ? <img src={livroDetalhe.capa_url} alt="" className="w-full h-full object-cover" />
                  : <BookOpen size={20} strokeWidth={1.5} color="#B8B0CC" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[17px] font-semibold text-ink-900 leading-tight mb-1"
                  style={{fontFamily:'Newsreader,Georgia,serif'}}>{livroDetalhe.titulo}</p>
                <p className="text-[13px] text-ink-400 font-medium mb-3">{livroDetalhe.autor?.split(',')[0]?.trim()}</p>
                {livroDetalhe.perfil && (
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center"
                      style={{background:'linear-gradient(135deg,#6E50F0,#3F27B0)'}}>
                      {livroDetalhe.perfil.foto_url
                        ? <img src={livroDetalhe.perfil.foto_url} alt="" className="w-full h-full object-cover" />
                        : <span className="text-[8px] font-bold text-white">{livroDetalhe.perfil.nome?.charAt(0)}</span>}
                    </div>
                    <span className="text-[12px] text-ink-500 font-semibold">{livroDetalhe.perfil.nome}</span>
                  </div>
                )}
              </div>
            </div>
            {livroDetalhe.descricao && (
              <p className="text-[13px] text-ink-500 leading-relaxed mb-4 line-clamp-5">{livroDetalhe.descricao}</p>
            )}
            <div className="flex gap-2">
              <button onClick={() => { setLivroDetalhe(null); navigate(`/biblioteca/${livroDetalhe.perfil?.slug}?livro=${livroDetalhe.id}`); }}
                className="flex-1 py-[13px] rounded-[14px] text-[14px] font-extrabold border-0 cursor-pointer text-white"
                style={{background:'linear-gradient(150deg,#6E50F0,#3F27B0)'}}>
                Solicitar empréstimo
              </button>
              <button onClick={() => setLivroDetalhe(null)}
                className="py-[13px] px-4 rounded-[14px] text-[14px] font-semibold border border-line bg-paper-2 cursor-pointer text-ink-700">
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Rastreio do empréstimo */}
      {emprestimoDetalhe && (() => {
        const emp = emprestimoDetalhe;
        const livro = emp.livros;
        const diasRestantes = emp.data_devolucao
          ? Math.ceil((new Date(emp.data_devolucao + 'T12:00:00') - new Date()) / 86400000)
          : null;
        const atrasado = diasRestantes !== null && diasRestantes < 0;
        const stages = [
          { label: 'Pedido enviado', desc: emp.role === 'leitor' ? `Para ${emp.outraPessoa?.nome || '...'}` : `De ${emp.outraPessoa?.nome || '...'}` },
          { label: 'Aprovado', desc: 'Pedido aceito pelo dono' },
          { label: 'Com o leitor', desc: emp.status === 'em_andamento'
              ? (emp.data_devolucao ? `Devolucao ate ${new Date(emp.data_devolucao + 'T12:00:00').toLocaleDateString('pt-BR')}` : 'Sem prazo definido')
              : 'Aguardando confirmacao de entrega' },
          { label: 'Devolvido', desc: 'Emprestimo concluido' },
        ];
        const modalStep = emp.status === 'devolvido' ? 3
          : emp.status === 'em_andamento' ? 2
          : emp.status === 'aprovado' ? 1 : 0;
        const whatsappOutro = emp.outraPessoa?.whatsapp;
        const whatsappLink = whatsappOutro ? `https://wa.me/55${whatsappOutro.replace(/\D/g, '')}` : null;
        const emAndamento = emp.status === 'em_andamento';
        return (
          <div className="fixed inset-0 flex items-end lg:items-center justify-center z-50 px-4 pb-6 lg:pb-0"
            style={{background:'rgba(33,28,43,0.42)'}}
            onClick={() => setEmprestimoDetalhe(null)}>
            <div className="bg-white rounded-[28px] p-5 w-full max-w-sm" onClick={e => e.stopPropagation()}>
              <div className="lg:hidden w-10 h-1 rounded-full bg-line mx-auto mb-5" />

              {/* Book info */}
              <div className="flex gap-3 mb-5 pb-5" style={{borderBottom:'1px solid #F2EEE9'}}>
                <div className="w-[46px] h-[68px] rounded-[9px] overflow-hidden bg-paper-2 flex items-center justify-center flex-shrink-0">
                  {livro?.capa_url
                    ? <img src={livro.capa_url} alt="" className="w-full h-full object-cover" />
                    : <BookOpen size={16} strokeWidth={1.5} color="#B8B0CC" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[16px] font-semibold text-ink-900 leading-tight m-0 mb-1"
                    style={{fontFamily:'Newsreader,Georgia,serif'}}>{livro?.titulo}</p>
                  <p className="text-[12px] text-ink-400 font-medium m-0 mb-2">{livro?.autor?.split(',')[0]?.trim()}</p>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[11.5px] font-semibold rounded-full px-2.5 py-[3px]"
                      style={{background:'#F0ECFE', color:'#5B3CE4'}}>
                      {emp.role === 'leitor' ? 'Voce e o leitor' : 'Voce e o dono'}
                    </span>
                    {atrasado && (
                      <span className="text-[11.5px] font-semibold rounded-full px-2.5 py-[3px]"
                        style={{background:'#FFEDE6', color:'#C5462A'}}>
                        {Math.abs(diasRestantes)}d em atraso
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div className="mb-5">
                {stages.map((stage, i) => {
                  const done = i < modalStep;
                  const current = i === modalStep;
                  return (
                    <div key={i} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{background: done ? '#1FA971' : current ? '#EDE9FF' : '#F3EFE9'}}>
                          {done
                            ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                            : <span className="rounded-full" style={{width: current ? 10 : 8, height: current ? 10 : 8, background: current ? '#5B3CE4' : '#D4CDE0'}} />
                          }
                        </div>
                        {i < stages.length - 1 && (
                          <div className="w-[2px] min-h-[18px] flex-1 my-[2px]"
                            style={{background: done ? '#1FA971' : '#F3EFE9'}} />
                        )}
                      </div>
                      <div className="pb-4 flex-1 min-w-0">
                        <p className="text-[13px] font-semibold m-0 leading-none mb-[3px]"
                          style={{color: done ? '#1FA971' : current ? '#211C2B' : '#9A93A4'}}>
                          {stage.label}
                        </p>
                        <p className="text-[11.5px] m-0" style={{color: (done || current) ? '#6E6878' : '#BDB7C5'}}>
                          {stage.desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* WhatsApp do outro */}
              {(emp.status === 'aprovado' || emAndamento) && (
                <div className="mb-4 p-3 rounded-[13px]" style={{background:'#F0FEF4', border:'1px solid #C6F1DB'}}>
                  <p className="text-[11px] font-bold text-ink-400 mb-2 uppercase tracking-wide">
                    WhatsApp {emp.role === 'leitor' ? 'do dono' : 'do leitor'}
                  </p>
                  {whatsappLink ? (
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-[14px] font-semibold text-ink-700">{whatsappOutro}</span>
                      <a href={whatsappLink} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1.5 no-underline px-3 py-1.5 rounded-[10px] text-[12px] font-bold text-white"
                        style={{background:'#25D366'}}>
                        <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                        Abrir WhatsApp
                      </a>
                    </div>
                  ) : (
                    <p className="text-[12px] text-ink-400">{emp.outraPessoa?.nome || 'A outra pessoa'} nao cadastrou o WhatsApp ainda.</p>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col gap-2">
                {/* Pendente — dono aprova ou recusa */}
                {emp.role === 'dono' && emp.status === 'pendente' && (
                  <div className="flex gap-2">
                    <button onClick={() => handleAprovarInline(emp)} disabled={aprovandoEmp}
                      className="flex-1 py-[13px] rounded-[14px] text-[14px] font-extrabold border-0 cursor-pointer text-white"
                      style={{background:'#1FA971', opacity: aprovandoEmp ? 0.6 : 1}}>
                      {aprovandoEmp ? 'Aprovando...' : 'Aprovar'}
                    </button>
                    <button onClick={() => handleRecusarInline(emp)} disabled={aprovandoEmp}
                      className="flex-1 py-[13px] rounded-[14px] text-[14px] font-semibold border border-line bg-paper-2 cursor-pointer text-ink-700">
                      Recusar
                    </button>
                  </div>
                )}

                {/* Aprovado — dono confirma entrega, leitor aguarda */}
                {emp.status === 'aprovado' && emp.role === 'dono' && (
                  <button onClick={() => handleConfirmarEntregaDono(emp)} disabled={salvandoEntrega}
                    className="w-full py-[13px] rounded-[14px] text-[14px] font-extrabold border-0 cursor-pointer text-white"
                    style={{background:'#5B3CE4', opacity: salvandoEntrega ? 0.6 : 1}}>
                    {salvandoEntrega ? 'Confirmando...' : 'Confirmar entrega'}
                  </button>
                )}
                {emp.status === 'aprovado' && emp.role === 'leitor' && (
                  <p className="text-[12px] text-ink-400 text-center py-2">Combinem a entrega pelo WhatsApp. O dono vai confirmar quando entregar.</p>
                )}

                {/* Em andamento — confirmacao bilateral de devolucao */}
                {emAndamento && emp.role === 'leitor' && (
                  emp.devolucao_confirmada_leitor
                    ? <p className="text-[12px] text-ink-400 text-center py-2">Devolucao registrada. Aguardando o dono confirmar.</p>
                    : <button onClick={() => handleConfirmarDevolucaoLeitor(emp)} disabled={salvandoDevolucao}
                        className="w-full py-[13px] rounded-[14px] text-[14px] font-extrabold border-0 cursor-pointer text-white"
                        style={{background:'#5B3CE4', opacity: salvandoDevolucao ? 0.6 : 1}}>
                        {salvandoDevolucao ? 'Confirmando...' : 'Devolvi o livro'}
                      </button>
                )}
                {emAndamento && emp.role === 'dono' && emp.prazo_dias === 0 && !emp.data_devolucao && (
                  <button onClick={() => handleSolicitarDevolucao(emp)} disabled={salvandoDevolucao}
                    className="w-full py-[13px] rounded-[14px] text-[14px] font-extrabold border-0 cursor-pointer text-white mb-2"
                    style={{background:'#5B3CE4', opacity: salvandoDevolucao ? 0.6 : 1}}>
                    {salvandoDevolucao ? 'Solicitando...' : 'Solicitar devolução'}
                  </button>
                )}
                {emAndamento && emp.role === 'dono' && (
                  <button onClick={() => handleConfirmarDevolucaoDono(emp)} disabled={salvandoDevolucao}
                    className="w-full py-[13px] rounded-[14px] text-[14px] font-extrabold border-0 cursor-pointer text-white mb-2"
                    style={{background:'#1FA971', opacity: salvandoDevolucao ? 0.6 : 1}}>
                    {salvandoDevolucao ? 'Confirmando...' : 'Recebi de volta'}
                  </button>
                )}
                {emAndamento && emp.role === 'dono' && atrasado && (
                  <button onClick={() => handleReportarInadimplente(emp)} disabled={salvandoDevolucao}
                    className="w-full py-[11px] rounded-[14px] text-[13px] font-semibold cursor-pointer mb-2"
                    style={{background:'#FFF5F3', color:'#C5462A', border:'1px solid #FFCFC7', opacity: salvandoDevolucao ? 0.6 : 1}}>
                    Leitor nao devolveu — bloquear conta
                  </button>
                )}

                <div className="flex gap-2">
                  {emp.status !== 'devolvido' && !emAndamento && (emp.status !== 'pendente' || emp.role === 'leitor') && (
                    <button onClick={() => handleCancelar(emp)}
                      className="flex-1 py-[13px] rounded-[14px] text-[14px] font-semibold border cursor-pointer"
                      style={{borderColor:'#FFCFC7', color:'#C5462A', background:'#FFF5F3'}}>
                      {emp.status === 'pendente' ? 'Cancelar pedido' : 'Cancelar emprestimo'}
                    </button>
                  )}
                  <button onClick={() => setEmprestimoDetalhe(null)}
                    className="flex-1 py-[13px] rounded-[14px] text-[14px] font-semibold border border-line bg-paper-2 cursor-pointer text-ink-700">
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      <div className="relative bg-paper overflow-hidden lg:flex" style={{height:'100dvh'}}>
        <SidebarDesktop {...sp} />
        {/* Content + right panel wrapper */}
        <div className="absolute inset-0 overflow-hidden lg:static lg:flex-1 lg:flex">
        {/* Scrollable main column */}
        <div className="h-full overflow-y-auto overflow-x-hidden pb-[104px] lg:flex-1 lg:pb-8" style={{scrollbarWidth:'none'}}>

          {/* Safe area spacer */}
          <div style={{height:'env(safe-area-inset-top)', minHeight:20}} />

          {/* Logo — mobile only (desktop tem na sidebar) */}
          <div className="lg:hidden px-5 pt-4 pb-1">
            <Logo size="sm" />
          </div>

          {/* Top header — mobile only */}
          <div className="flex items-center justify-between px-5 pt-3 lg:hidden">
            <div className="flex items-center gap-[11px]">
              <div onClick={() => setTela('perfil')} className="cursor-pointer lg:hidden">
                <AvatarPremium fotoUrl={fotoPerfil} nome={nomeUsuario} isPremium={isPago} size={40} radius="13px" />
              </div>
              <div>
                <p className="text-[12.5px] text-ink-400 font-semibold m-0 leading-none mb-0.5">{saudacao}</p>
                <div className="flex items-center gap-1.5">
                  <p onClick={() => setTela('perfil')} className="text-[15px] font-extrabold m-0 tracking-tight text-ink-900 leading-none cursor-pointer">{nomeUsuario}</p>
                  {isPago
                    ? <span className="text-[10px] font-extrabold px-1.5 py-[2px] rounded-full leading-none" style={{background:'#FFF3CC',color:'#B87A00'}}>Premium</span>
                    : <button onClick={() => openUpgrade()} className="text-[10px] font-extrabold px-1.5 py-[2px] rounded-full leading-none border-0 cursor-pointer" style={{background:'#F0ECFE',color:'#5B3CE4'}}>Seja Premium</button>
                  }
                </div>
              </div>
            </div>
            <div className="flex gap-2.5">
              <button
                onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/biblioteca/${userSlug}`); showToast('Link copiado!', 'success'); }}
                className="w-10 h-10 rounded-[13px] bg-white border border-line flex items-center justify-center text-ink-700 flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
              </button>
              <button onClick={() => setTela('notificacoes')}
                className="w-10 h-10 rounded-[13px] bg-white border border-line flex items-center justify-center text-ink-700 relative flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10 5a2 2 0 1 1 4 0a7 7 0 0 1 4 6v3a4 4 0 0 0 2 3H4a4 4 0 0 0 2-3v-3a7 7 0 0 1 4-6"/><path d="M9 17v1a3 3 0 0 0 6 0v-1"/></svg>
                {notifPendentes > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[17px] h-[17px] px-1 rounded-full text-white text-[10px] font-extrabold leading-[17px] text-center border-2 border-paper"
                    style={{background:'#FF7A59'}}>
                    {notifPendentes > 9 ? '9+' : notifPendentes}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Desktop hero */}
          <div className="hidden lg:flex items-center justify-between gap-8 mx-6 mt-6 rounded-[20px] relative overflow-hidden"
            style={{background:'linear-gradient(120deg,#EEEBFC 0%,#F3F0FC 48%,#F7F2FB 100%)', border:'1px solid #EDE9F5', padding:'30px 34px'}}>
            <span style={{position:'absolute', right:-60, top:-60, width:280, height:280, borderRadius:'50%', background:'radial-gradient(circle,rgba(91,60,228,.18),transparent 65%)', pointerEvents:'none'}} />
            <div>
              <h1 style={{fontSize:30, fontWeight:800, letterSpacing:'-.03em', margin:0, marginBottom:6, color:'#211C2B'}}>
                {getSaudacao()}{nomeUsuario ? `, ${nomeUsuario.split(' ')[0]}` : ''}!
              </h1>
              <p style={{fontSize:14, fontWeight:500, color:'#6E6878', margin:0, lineHeight:1.4}}>
                {emprestimosAtivos.length > 0
                  ? `Você tem ${emprestimosAtivos.length} empréstimo${emprestimosAtivos.length !== 1 ? 's' : ''} ativo${emprestimosAtivos.length !== 1 ? 's' : ''}. Que tal explorar mais?`
                  : 'Seu acervo está em dia. Que tal emprestar um livro hoje?'}
              </p>
              <div style={{display:'flex', gap:10, marginTop:18}}>
                {[
                  [livros.length, 'na estante'],
                  [emprestimosAtivos.filter(e => e.role === 'dono').length, 'emprestados'],
                  [emprestimosAtivos.filter(e => e.role === 'leitor').length, 'lendo agora'],
                ].map(([n, label]) => (
                  <div key={label} style={{background:'rgba(255,255,255,.72)', border:'1px solid rgba(255,255,255,.9)', borderRadius:14, padding:'10px 16px', backdropFilter:'blur(4px)'}}>
                    <b style={{display:'block', fontSize:22, fontWeight:800, color:'#211C2B'}}>{n}</b>
                    <span style={{fontSize:12, color:'#6E6878', fontWeight:600}}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{maxWidth:280, textAlign:'right', flexShrink:0}}>
              <p style={{fontSize:10, letterSpacing:'.2em', fontWeight:700, color:'#5B3CE4', margin:0}}>FRASE DO DIA</p>
              <q style={{display:'block', fontSize:18, fontWeight:600, fontStyle:'italic', lineHeight:1.3, margin:'8px 0 6px', color:'#3D3748', fontFamily:'Newsreader,Georgia,serif'}}>{fraseDoDia.frase}</q>
              <cite style={{fontSize:12, color:'#9A93A4', fontStyle:'normal', fontWeight:600}}>— {fraseDoDia.autor}</cite>
            </div>
          </div>

          {/* Quote card — mobile only */}
          <div className="lg:hidden relative mx-5 mt-3 rounded-[24px] overflow-hidden"
            style={{background:'linear-gradient(125deg,#6E50F0 0%,#4A2DC7 58%,#3F27B0 100%)', boxShadow:'0 10px 24px rgba(63,39,176,0.28)', padding:'22px 22px 20px'}}>
            <span className="absolute right-3.5 font-serif text-[120px] leading-none select-none pointer-events-none"
              style={{top:'-22px', color:'rgba(255,255,255,0.16)', fontFamily:'Newsreader,Georgia,serif'}}>"</span>
            <p className="text-[11px] font-extrabold tracking-[0.08em] uppercase m-0 mb-[9px]" style={{color:'rgba(255,255,255,0.66)'}}>Frase do dia</p>
            <p className="text-[18px] leading-[1.32] italic font-medium text-white tracking-tight m-0 max-w-[88%] relative z-10"
              style={{fontFamily:'Newsreader,Georgia,serif'}}>
              {fraseDoDia.frase}
            </p>
            <p className="text-[11.5px] font-bold tracking-[0.03em] uppercase mt-3 mb-0" style={{color:'rgba(255,255,255,0.6)'}}>— {fraseDoDia.autor}</p>
          </div>

          {/* Sua Próxima leitura */}
          <div>
          {sugestoesLeitura.length > 0 && (
            <div className="mt-6">
              <div className="px-5 mb-3 flex items-center justify-between">
                <h2 className="text-[17px] font-extrabold tracking-tight m-0 text-ink-900">Sua próxima leitura</h2>
                <button onClick={() => setTela('explorar')} className="text-[13px] font-semibold border-0 bg-transparent cursor-pointer" style={{color:'#5B3CE4'}}>Ver tudo</button>
              </div>
              <div className="flex gap-[12px] overflow-x-auto px-5 pb-1.5" style={{scrollbarWidth:'none'}}>
                {(isPago ? sugestoesLeitura : sugestoesLeitura.slice(0, LIMITES.sugestoes_mes)).map(livro => (
                  <button key={livro.id}
                    onClick={() => setLivroDetalhe(livro)}
                    className="flex-shrink-0 w-[130px] bg-transparent border-0 p-0 cursor-pointer text-left" >
                    <div className="w-full rounded-[12px] overflow-hidden bg-paper-2 flex items-center justify-center mb-[9px]"
                      style={{aspectRatio:'2/3', boxShadow: cardShadow}}>
                      {livro.capa_url
                        ? <img src={livro.capa_url} alt="" className="w-full h-full object-cover" />
                        : <BookOpen size={24} strokeWidth={1.5} color="#B8B0CC" />}
                    </div>
                    <p className="text-[13px] font-semibold text-ink-900 m-0 leading-tight line-clamp-2 mb-[3px]"
                      style={{fontFamily:'Newsreader,Georgia,serif'}}>
                      {livro.titulo}
                    </p>
                    <p className="text-[11px] text-ink-400 font-medium m-0 truncate mb-[6px]">{livro.autor?.split(',')[0]?.trim()}</p>
                    <div className="flex items-center gap-[5px]">
                      <AvatarPremium fotoUrl={livro.perfil.foto_url} nome={livro.perfil.nome} isPremium={livro.perfil.plano === 'pago'} size={18} />
                      <span className="text-[11px] text-ink-400 font-semibold truncate">{livro.perfil.nome}</span>
                    </div>
                  </button>
                ))}
                {!isPago && sugestoesLeitura.length > LIMITES.sugestoes_mes && (
                  <button onClick={() => openUpgrade('sugestoes')}
                    className="flex-shrink-0 w-[130px] border-0 p-0 cursor-pointer text-left bg-transparent">
                    <div className="w-full rounded-[12px] flex flex-col items-center justify-center mb-[9px]"
                      style={{aspectRatio:'2/3', background:'linear-gradient(145deg,#F0ECFE,#E8E4F0)', boxShadow:'3px 3px 12px rgba(33,28,43,0.10)'}}>
                      <span className="text-[22px] mb-1">+</span>
                      <span className="text-[11px] font-bold text-center px-2 leading-tight" style={{color:'#5B3CE4'}}>Ver mais com Premium</span>
                    </div>
                  </button>
                )}
              </div>
            </div>
          )}
          </div>

          {/* Atraso warning */}
          {emprestimosAtrasados > 0 && (
            <div className="mx-5 mt-3">
              <div className="flex items-center justify-between px-4 py-3 rounded-[13px]" style={{background:'#FFEDE6'}}>
                <p className="text-[12.5px] font-semibold m-0" style={{color:'#C5462A'}}>
                  {emprestimosAtrasados} livro{emprestimosAtrasados !== 1 ? 's' : ''} em atraso
                </p>
                <button onClick={() => setTela('notificacoes')} className="text-[12px] font-bold ml-3 whitespace-nowrap flex-shrink-0 bg-transparent border-0 cursor-pointer" style={{color:'#C5462A'}}>
                  Ver →
                </button>
              </div>
            </div>
          )}


          {/* Minha Biblioteca */}
          <div className="mt-6 px-5">
            {menuOrdem && <div className="fixed inset-0 z-10" onClick={() => setMenuOrdem(false)} />}

            <h2 className="text-[17px] font-extrabold tracking-tight m-0 mb-3 text-ink-900">Minha Biblioteca</h2>

            {/* Toolbar: busca + ordenação + visualização */}
            <div className="flex items-center gap-2 mb-3" onClick={() => menuOrdem && setMenuOrdem(false)}>
              <div className="flex-1 relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9A93A4" strokeWidth="2.2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                <input type="text" placeholder="Buscar na biblioteca…" value={busca}
                  onChange={e => setBusca(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-[13px] font-medium bg-white border border-line rounded-[10px] outline-none text-ink-900"
                />
              </div>
              <div className="relative" onClick={e => e.stopPropagation()}>
                <button onClick={() => setMenuOrdem(v => !v)}
                  className="w-9 h-9 rounded-[10px] bg-white border border-line flex items-center justify-center flex-shrink-0"
                  style={{color: menuOrdem ? '#5B3CE4' : '#6B6478'}}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M3 6h18M7 12h10M11 18h2"/></svg>
                </button>
                {menuOrdem && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-line rounded-[12px] overflow-hidden z-30 w-36"
                    style={{boxShadow:'0 8px 20px rgba(0,0,0,0.12)'}}>
                    {[['az','A–Z'],['estrelas','Melhor nota']].map(([val, label]) => (
                      <button key={val} onClick={() => { setOrdenacao(val); setMenuOrdem(false); }}
                        className="w-full text-left px-3 py-2.5 text-[13px] font-semibold border-0 cursor-pointer"
                        style={ordenacao === val ? {color:'#5B3CE4', background:'#F0ECFD'} : {color:'#3D3748', background:'transparent'}}>
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={() => setVisualizacao(v => { const n = v === 'lista' ? 'grade' : 'lista'; localStorage.setItem('acervo_visualizacao', n); return n; })}
                className="w-9 h-9 rounded-[10px] bg-white border border-line flex items-center justify-center flex-shrink-0"
                style={{color:'#6B6478'}}>
                {visualizacao === 'lista'
                  ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                  : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                }
              </button>
            </div>

            {/* Tab selector */}
            <div style={{display:'flex',gap:8,marginBottom:20}}>
              {[['livros','Meus Livros'],['emprestimos','Meus Empréstimos']].map(([key, label]) => (
                <button key={key} onClick={() => setAbaBiblioteca(key)}
                  style={{padding:'8px 20px',borderRadius:999,fontWeight:700,fontSize:13,border:'none',cursor:'pointer',
                    background: abaBiblioteca === key ? '#5B3CE4' : '#EDE8F5',
                    color: abaBiblioteca === key ? '#fff' : '#6E6878'}}>
                  {label}
                </button>
              ))}
            </div>

            {abaBiblioteca === 'emprestimos' ? (
              <>
                {emprestimosAtivos.length === 0 ? (
                  <div className="text-center py-10 px-6">
                    <div className="w-14 h-14 rounded-[18px] flex items-center justify-center mx-auto mb-3"
                      style={{background:'linear-gradient(150deg,#EDE9FF,#D8D0FF)'}}>
                      <BookOpen size={28} strokeWidth={1.4} color="#5B3CE4" />
                    </div>
                    <p className="text-[15px] font-extrabold text-ink-900 mb-2">Nenhum empréstimo ativo</p>
                    <p className="text-[13px] text-ink-400 leading-relaxed mb-5">Explore livros disponíveis perto de você e solicite um empréstimo.</p>
                    <button onClick={() => setTela('explorar')}
                      className="w-full py-3 rounded-[14px] text-[14px] font-extrabold text-white border-0 cursor-pointer"
                      style={{background:'#5B3CE4'}}>
                      Descobrir livros
                    </button>
                  </div>
                ) : emprestimosAtivos.map(emp => {
                  const livro = emp.livros;
                  if (!livro) return null;
                  const hasEntregaCard = emp.entrega_tipo === 'pessoalmente' && !!(emp.entrega_local || emp.entrega_data);
                  const hasDevolucaoCard = !!(emp.devolucao_local || emp.devolucao_data);
                  const stageCount = 4 + (hasEntregaCard ? 1 : 0) + (hasDevolucaoCard ? 1 : 0);
                  const step = emp.status === 'devolvido'
                    ? stageCount - 1
                    : emp.status === 'em_andamento' && hasDevolucaoCard
                      ? 2 + (hasEntregaCard ? 1 : 0) + 1
                      : emp.status === 'em_andamento'
                        ? 2 + (hasEntregaCard ? 1 : 0)
                        : emp.status === 'aprovado' && hasEntregaCard ? 2
                        : emp.status === 'aprovado' ? 1 : 0;
                  const diasRestantes = emp.data_devolucao
                    ? Math.ceil((new Date(emp.data_devolucao) - new Date()) / 86400000) : null;
                  const atrasado = diasRestantes !== null && diasRestantes < 0;
                  const statusLabel = emp.status === 'pendente' ? 'Pedido enviado'
                    : emp.status === 'aprovado' ? 'Aprovado'
                    : 'Em andamento';
                  return (
                    <div key={emp.id} className="bg-white border border-line rounded-[18px] p-4 mb-[10px] cursor-pointer"
                      style={{boxShadow: cardShadow}} onClick={() => setEmprestimoDetalhe(emp)}>
                      <p className="text-[10px] font-extrabold tracking-[0.07em] uppercase m-0 mb-[10px]"
                        style={{color: emp.role === 'leitor' ? '#5B3CE4' : '#1FA971'}}>
                        {emp.role === 'leitor' ? 'Estou pegando' : 'Estou emprestando'}
                      </p>
                      <div className="flex gap-3">
                        <div className="w-11 h-16 rounded-[8px] overflow-hidden bg-paper-2 flex items-center justify-center flex-shrink-0">
                          {livro.capa_url
                            ? <img src={livro.capa_url} alt="" className="w-full h-full object-cover" />
                            : <BookOpen size={18} strokeWidth={1.5} color="#B8B0CC" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[15px] font-semibold m-0 leading-tight line-clamp-2 text-ink-900 mb-1"
                            style={{fontFamily:'Newsreader,Georgia,serif'}}>{livro.titulo}</p>
                          <p className="text-[12px] text-ink-400 font-semibold m-0 truncate mb-2">{emp.outraPessoa?.nome}</p>
                          <div className="flex items-center gap-2">
                            <span className="text-[12px] font-bold" style={{color: atrasado ? '#FF7A59' : '#6E6878'}}>
                              {statusLabel}{diasRestantes !== null ? ` · ${Math.abs(diasRestantes)}d` : ''}
                            </span>
                            {emp.role === 'dono' && emp.status === 'pendente' && (
                              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                                style={{background:'#ECE7FE', color:'#3F27B0'}}>responder</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-[7px] mt-3">
                        {Array.from({ length: stageCount }).map((_, s) => (
                          <i key={s} className="flex-1 h-[3px] rounded-full block not-italic"
                            style={{background: s <= step ? '#1FA971' : '#F3EFE9'}} />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              <>

            {carregando ? (
              <p className="text-sm text-ink-400 text-center py-8">Carregando…</p>
            ) : livrosOrdenados.length === 0 ? (
              <div className="text-center py-12 px-6">
                <div className="w-16 h-16 rounded-[22px] flex items-center justify-center mx-auto mb-4"
                  style={{background:'linear-gradient(150deg,#EDE9FF,#D8D0FF)'}}>
                  <BookOpen size={32} strokeWidth={1.4} color="#5B3CE4" />
                </div>
                <p className="text-[17px] font-extrabold text-ink-900 mb-2">Sua biblioteca está vazia</p>
                <p className="text-[13px] text-ink-400 mb-6 leading-relaxed">
                  Adicione os livros que você tem em casa e comece a emprestar para quem está perto de você.
                </p>
                <button onClick={() => setTela('cadastrar')}
                  className="w-full py-3.5 rounded-[14px] text-white font-bold text-[14px] mb-3"
                  style={{background:'linear-gradient(150deg,#6E50F0,#3F27B0)'}}>
                  + Adicionar meu primeiro livro
                </button>
                <button onClick={() => setTela('explorar')}
                  className="w-full py-3 rounded-[14px] text-[13px] font-semibold border border-line text-ink-500 bg-white">
                  Explorar livros da comunidade
                </button>
              </div>
            ) : livrosFiltradosCat.length === 0 ? (
              <div className="text-center py-10 px-6">
                <p className="text-[14px] font-bold text-ink-900 mb-1">Nada encontrado</p>
                <p className="text-[12.5px] text-ink-400 mb-4">Nenhum livro corresponde a esse filtro.</p>
                <button onClick={() => { setCategoria('Tudo'); setBusca(''); }}
                  className="text-[13px] font-semibold px-5 py-2.5 rounded-[11px] border border-line text-ink-500 bg-white">
                  Ver todos os livros
                </button>
              </div>
            ) : visualizacao === 'grade' ? (
              <div className="grid grid-cols-2 gap-[10px]">
                {livrosFiltradosCat.map(livro => (
                  <div key={livro.id}
                    onClick={() => { setLivroSelecionado(livro); setTela('detalhe'); }}
                    className="bg-white border border-line rounded-[18px] overflow-hidden cursor-pointer p-3"
                    style={{boxShadow: cardShadow}}>
                    <div className="w-full rounded-[9px] overflow-hidden bg-paper-2 flex items-center justify-center mb-[10px]" style={{aspectRatio:'2/3'}}>
                      {livro.capa_url
                        ? <img src={livro.capa_url} alt="" className="w-full h-full object-cover" />
                        : <BookOpen size={24} strokeWidth={1.5} color="#B8B0CC" />}
                    </div>
                    <p className="text-[13px] font-semibold m-0 leading-[1.2] tracking-tight line-clamp-2 text-ink-900 mb-[3px]"
                      style={{fontFamily:'Newsreader,Georgia,serif'}}>
                      {livro.titulo}
                    </p>
                    <p className="text-[11px] text-ink-400 font-medium m-0 truncate mb-[7px]">{livro.autor?.split(',')[0]?.trim()}</p>
                    {(() => {
                      if (livro.especial) return (
                        <span className="inline-flex items-center gap-[4px] text-[11px] font-semibold px-[9px] py-[3px] rounded-full"
                          style={{background:'#FFF8E1', color:'#B87A00'}}>
                          <span className="w-[6px] h-[6px] rounded-full inline-block flex-shrink-0" style={{background:'#E6A800'}} />
                          Especial
                        </span>
                      );
                      const estaFora = !livro.disponivel || !!emprestimoAtivoPorLivro[livro.id];
                      if (!estaFora) return null;
                      const prazo = infoPrazoBadge(livro.id);
                      return (
                        <span className="inline-flex items-center gap-[4px] text-[11px] font-semibold px-[9px] py-[3px] rounded-full"
                          style={{background: prazo?.bg || '#FBF1DC', color: prazo?.color || '#9A6B12'}}>
                          <span className="w-[6px] h-[6px] rounded-full inline-block flex-shrink-0" style={{background: prazo?.dot || '#E0A02E'}} />
                          {prazo?.label || 'Fora'}
                        </span>
                      );
                    })()}
                  {livro.disponivel && !livro.especial && (
                    <button
                      onClick={e => { e.stopPropagation(); setLivroParaRegistrar(livro); setModalRegistrarAberto(true); }}
                      className="text-[11px] font-bold px-2.5 py-[5px] rounded-full border-0 cursor-pointer text-white mt-1"
                      style={{background:'linear-gradient(150deg,#6E50F0,#3F27B0)', display:'block', width:'100%'}}>
                      Emprestar
                    </button>
                  )}
                  </div>
                ))}
              </div>
            ) : (
              <div>
                {livrosFiltradosCat.map((livro, idx) => (
                  <div key={livro.id}
                    onClick={() => { setLivroSelecionado(livro); setTela('detalhe'); }}
                    className="flex items-center gap-[13px] p-3 bg-white border border-line rounded-[18px] cursor-pointer"
                    style={{boxShadow: cardShadow, marginTop: idx > 0 ? 10 : 0}}>
                    <div className="w-11 h-16 rounded-[7px] overflow-hidden flex-shrink-0 bg-paper-2 flex items-center justify-center">
                      {livro.capa_url
                        ? <img src={livro.capa_url} alt="" className="w-full h-full object-cover" />
                        : <BookOpen size={20} strokeWidth={1.5} color="#B8B0CC" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[16px] font-semibold m-0 leading-[1.12] tracking-tight truncate text-ink-900"
                        style={{fontFamily:'Newsreader,Georgia,serif'}}>
                        {livro.titulo}
                      </p>
                      <p className="text-[12px] text-ink-400 mt-[3px] mb-1.5 font-semibold m-0 truncate">{livro.autor?.split(',')[0]?.trim()}</p>
                      <div className="flex items-center gap-[9px]">
                        <span>
                          {[1,2,3,4,5].map(s => (
                            <span key={s} className="text-[11px]"
                              style={{color: s <= livro.estrelas ? '#E0A02E' : '#BDB7C5'}}>★</span>
                          ))}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                      {(() => {
                        if (livro.especial) return (
                          <span className="inline-flex items-center gap-[5px] text-[12px] font-semibold px-[11px] py-[5px] rounded-full"
                            style={{background:'#FFF8E1', color:'#B87A00'}}>
                            <span className="w-[7px] h-[7px] rounded-full inline-block flex-shrink-0" style={{background:'#E6A800'}} />
                            Especial
                          </span>
                        );
                        const estaFora = !livro.disponivel || !!emprestimoAtivoPorLivro[livro.id];
                        if (!estaFora) return null;
                        const prazo = infoPrazoBadge(livro.id);
                        return (
                          <span className="inline-flex items-center gap-[5px] text-[12px] font-semibold px-[11px] py-[5px] rounded-full"
                            style={{background: prazo?.bg || '#FBF1DC', color: prazo?.color || '#9A6B12'}}>
                            <span className="w-[7px] h-[7px] rounded-full inline-block flex-shrink-0" style={{background: prazo?.dot || '#E0A02E'}} />
                            {prazo?.label || 'Fora'}
                          </span>
                        );
                      })()}
                      {livro.disponivel && !livro.especial && (
                        <button
                          onClick={e => { e.stopPropagation(); setLivroParaRegistrar(livro); setModalRegistrarAberto(true); }}
                          className="text-[11px] font-bold px-2.5 py-[5px] rounded-full border-0 cursor-pointer text-white"
                          style={{background:'linear-gradient(150deg,#6E50F0,#3F27B0)'}}>
                          Emprestar
                        </button>
                      )}
                      {clubesDoUsuario.length > 0 && (
                        <button
                          onClick={e => { e.stopPropagation(); setLivroParaAdicionar(livro); }}
                          className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-transparent border border-line text-ink-400">
                          + clube
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
              </>
            )}
          </div>
        </div>

        {/* Right panel — desktop only */}
        <div className="hidden lg:flex flex-col border-l border-line-soft overflow-y-auto" style={{width: 320, scrollbarWidth: 'none', background: '#F6F5FB'}}>
          <div style={{padding: '24px 20px 32px'}}>

            {/* Atividade dos amigos */}
            {atividadeAmigos.length > 0 && (
              <div style={{marginBottom: 24}}>
                <p style={{fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: INK400, margin: 0, marginBottom: 12}}>Atividade dos amigos</p>
                <div style={{background: '#fff', border: '1px solid #F2EEE9', borderRadius: 14, padding: '4px 14px', boxShadow: '0 1px 4px rgba(33,28,43,0.04)'}}>
                  {atividadeAmigos.map((item, idx) => (
                    <div key={item.id} style={{display: 'flex', gap: 11, padding: '11px 0', borderTop: idx > 0 ? '1px solid #F2EEE9' : 'none'}}>
                      <AvatarPremium fotoUrl={item.perfil?.foto_url} nome={item.perfil?.nome} size={30} />
                      <div style={{flex: 1, minWidth: 0}}>
                        <div style={{fontSize: 13, lineHeight: 1.4, color: '#3D3748'}}>
                          <b style={{fontWeight: 700, color: '#211C2B'}}>{item.perfil?.nome?.split(' ')[0]}</b>
                          {' '}adicionou{' '}
                          <b style={{fontWeight: 700, color: '#211C2B'}}>{item.titulo}</b>
                          {' '}ao acervo.
                        </div>
                        <span style={{fontSize: 11.5, color: INK400, marginTop: 2, display: 'block'}}>{tempoAtras(item.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Devoluções a vencer */}
            {emprestimosComPrazo.length > 0 && (
              <div style={{marginBottom: 24}}>
                <p style={{fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: INK400, margin: 0, marginBottom: 12}}>Devoluções a vencer</p>
                {emprestimosComPrazo.map(emp => {
                  const dias = Math.ceil((new Date(emp.data_devolucao + 'T12:00:00') - new Date()) / 86400000);
                  const diaColor = dias < 0 ? '#C5462A' : dias <= 3 ? '#E65100' : INK400;
                  return (
                    <div key={emp.id}
                      onClick={() => setEmprestimoDetalhe(emp)}
                      style={{display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 13, background: '#fff', border: '1px solid #F2EEE9', marginBottom: 7, cursor: 'pointer', boxShadow: '0 1px 3px rgba(33,28,43,0.04)'}}>
                      <div style={{width: 34, height: 50, borderRadius: 7, overflow: 'hidden', background: '#F3EFE9', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        {emp.livros?.capa_url
                          ? <img src={emp.livros.capa_url} alt="" style={{width:'100%', height:'100%', objectFit:'cover'}} />
                          : <BookOpen size={14} strokeWidth={1.5} color="#B8B0CC" />}
                      </div>
                      <div style={{flex: 1, minWidth: 0}}>
                        <p style={{fontSize: 12, fontWeight: 600, color: INK, margin: 0, lineHeight: 1.3, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'}}>{emp.livros?.titulo}</p>
                        <p style={{fontSize: 11, color: diaColor, margin: 0, marginTop: 3, fontWeight: 600}}>
                          {dias < 0 ? `${Math.abs(dias)}d em atraso` : dias === 0 ? 'Vence hoje' : `${dias}d restante${dias !== 1 ? 's' : ''}`}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}


          </div>
        </div>
        </div>

        {/* Bottom nav — mobile only */}
        <div className="absolute bottom-0 left-0 right-0 z-30 flex items-center px-[18px] lg:hidden"
          style={{paddingTop:10, paddingBottom: 'calc(10px + env(safe-area-inset-bottom))', background:'rgba(255,255,255,0.82)', backdropFilter:'blur(18px) saturate(160%)', WebkitBackdropFilter:'blur(18px) saturate(160%)', borderTop:'1px solid #F2EEE9'}}>
          <button onClick={() => setTela('acervo')}
            className="flex-1 flex flex-col items-center gap-1 bg-transparent border-0 cursor-pointer"
            style={{color: tela === 'acervo' ? '#5B3CE4' : '#9A93A4'}}>
            <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 19a9 9 0 0 1 9 0a9 9 0 0 1 9 0"/><path d="M3 6a9 9 0 0 1 9 0a9 9 0 0 1 9 0"/><line x1="3" y1="6" x2="3" y2="19"/><line x1="12" y1="6" x2="12" y2="19"/><line x1="21" y1="6" x2="21" y2="19"/></svg>
            <span className="text-[10px] font-semibold tracking-[0.01em]">Biblioteca</span>
          </button>
          <button onClick={() => setTela('explorar')}
            className="flex-1 flex flex-col items-center gap-1 bg-transparent border-0 cursor-pointer"
            style={{color: tela === 'explorar' ? '#5B3CE4' : '#9A93A4'}}>
            <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>
            <span className="text-[10px] font-semibold tracking-[0.01em]">Explorar</span>
          </button>
          <div className="flex-none mx-1.5">
            <button onClick={() => setTela('cadastrar')}
              className="w-[52px] h-[52px] rounded-[17px] border-0 cursor-pointer text-white flex items-center justify-center"
              style={{background:'linear-gradient(150deg,#6E50F0,#3F27B0)', marginTop:-18, boxShadow:'0 8px 18px rgba(91,60,228,0.40)'}}>
              <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </button>
          </div>
          <button onClick={() => setTela('amigos')}
            className="flex-1 flex flex-col items-center gap-1 bg-transparent border-0 cursor-pointer"
            style={{color: tela === 'amigos' ? '#5B3CE4' : '#9A93A4'}}>
            <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><polyline points="16 11 18 13 22 9"/></svg>
            <span className="text-[10px] font-semibold tracking-[0.01em]">Amigos</span>
          </button>
          <button onClick={() => setTela('clubes')}
            className="flex-1 flex flex-col items-center gap-1 bg-transparent border-0 cursor-pointer"
            style={{color: tela === 'clubes' ? '#5B3CE4' : '#9A93A4'}}>
            <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            <span className="text-[10px] font-semibold tracking-[0.01em]">Clubes</span>
          </button>
        </div>
      </div>

      {/* Modal tour da home — aparece após o onboarding */}
      {mostrarTourHome && (
        <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center font-sans px-0 lg:px-4"
          style={{ background: 'rgba(33,28,43,0.5)' }}>
          <div className="bg-white w-full lg:max-w-md rounded-t-[28px] lg:rounded-[28px] px-5 pt-5"
            style={{ paddingBottom: 'calc(28px + env(safe-area-inset-bottom))' }}>
            <div className="lg:hidden w-10 h-1 rounded-full bg-line mx-auto mb-5" />
            <p className="text-[22px] font-extrabold text-ink-900 mb-1">Sua biblioteca está pronta! 🎉</p>
            <p className="text-[14px] text-ink-400 mb-5">Veja o que você pode fazer por aqui:</p>
            <div className="flex flex-col gap-2.5 mb-6">
              {[
                { Icon: BookOpen,  cor: '#EDE9FF', iconCor: '#5B3CE4', nome: 'Estante', desc: 'Organize e gerencie seu acervo pessoal' },
                { Icon: Compass,   cor: '#E0F4FF', iconCor: '#0A7ADB', nome: 'Explorar', desc: 'Descubra livros disponíveis na comunidade' },
                { Icon: Users,     cor: '#FFF4E0', iconCor: '#E08A00', nome: 'Amigos', desc: 'Conecte-se com outros leitores' },
                { Icon: Building2, cor: '#FFE9E9', iconCor: '#D94040', nome: 'Clubes', desc: 'Participe de clubes de leitura' },
              ].map(({ Icon, cor, iconCor, nome, desc }) => (
                <div key={nome} className="flex items-center gap-3 bg-paper px-4 py-3 rounded-[13px]">
                  <div className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0"
                    style={{ background: cor }}>
                    <Icon size={18} strokeWidth={1.7} color={iconCor} />
                  </div>
                  <div>
                    <p className="text-[14px] font-bold text-ink-900">{nome}</p>
                    <p className="text-[12px] text-ink-400">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={() => { localStorage.setItem(`tour_home_${userId}`, 'done'); setMostrarTourHome(false); }}
              className="w-full py-3.5 rounded-[14px] text-white font-bold text-[15px] border-0 cursor-pointer"
              style={{ background: 'linear-gradient(150deg,#6E50F0,#3F27B0)' }}>
              Começar a explorar!
            </button>
          </div>
        </div>
      )}

      {/* Modal — registrar empréstimo direto (Fluxo B) */}
      {modalRegistrarAberto && (
        <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center bg-black/40 px-0 lg:px-4"
          onClick={e => { if (e.target === e.currentTarget) { setModalRegistrarAberto(false); setBuscaRegistrar(''); setLeitorRegistrar(null); setNomeExterno(''); setWppExterno(''); setExpandirExterno(false); } }}>
          <div className="w-full lg:max-w-md bg-white rounded-t-3xl lg:rounded-2xl shadow-2xl overflow-hidden max-h-[90dvh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-line-soft flex-shrink-0">
              <p className="text-[15px] font-extrabold text-ink-900">Registrar empréstimo</p>
              <button onClick={() => { setModalRegistrarAberto(false); setBuscaRegistrar(''); setLeitorRegistrar(null); setNomeExterno(''); setWppExterno(''); setExpandirExterno(false); }}
                className="w-8 h-8 rounded-full flex items-center justify-center text-ink-400 hover:text-ink-700 hover:bg-paper transition-colors text-xl leading-none border-0 bg-transparent cursor-pointer">
                ×
              </button>
            </div>

            <div className="overflow-y-auto flex-1 px-5 pb-6 pt-4" style={{scrollbarWidth:'none'}}>
              {/* Livro */}
              {livroParaRegistrar && (
                <div className="flex gap-3 pb-4 mb-5 border-b border-line-soft">
                  <div className="w-10 h-14 rounded-[8px] overflow-hidden bg-paper-2 flex items-center justify-center flex-shrink-0">
                    {livroParaRegistrar.capa_url
                      ? <img src={livroParaRegistrar.capa_url} alt="" className="w-full h-full object-cover" />
                      : <BookOpen size={18} strokeWidth={1.5} color="#B8B0CC" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[14px] font-semibold text-ink-900 leading-tight truncate">{livroParaRegistrar.titulo}</p>
                    <p className="text-[12px] text-ink-400 mt-0.5">{livroParaRegistrar.autor?.split(',')[0]?.trim()}</p>
                  </div>
                </div>
              )}

              {/* Leitor selecionado */}
              {leitorRegistrar ? (
                <div className="flex items-center gap-3 p-3 rounded-[13px] border border-line bg-paper-2 mb-4">
                  <div className="w-9 h-9 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0 text-[13px] font-bold text-white"
                    style={{background:'linear-gradient(150deg,#6E50F0,#3F27B0)'}}>
                    {leitorRegistrar.foto_url
                      ? <img src={leitorRegistrar.foto_url} alt="" className="w-full h-full object-cover" />
                      : (leitorRegistrar.nome || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] font-semibold text-ink-900 truncate">{leitorRegistrar.nome}</p>
                    {leitorRegistrar.username && <p className="text-[12px] text-ink-400">@{leitorRegistrar.username}</p>}
                  </div>
                  <button onClick={() => { setLeitorRegistrar(null); setBuscaRegistrar(''); }}
                    className="text-[12px] font-semibold text-ink-400 hover:text-ink-700 border-0 bg-transparent cursor-pointer flex-shrink-0">
                    Trocar
                  </button>
                </div>
              ) : expandirExterno ? (
                /* Modo externo (sem app) */
                <div className="mb-4">
                  <button onClick={() => { setExpandirExterno(false); setNomeExterno(''); setWppExterno(''); }}
                    className="flex items-center gap-1 text-[12px] font-semibold text-ink-400 mb-3 bg-transparent border-0 cursor-pointer p-0 hover:text-ink-700">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="m15 18-6-6 6-6"/></svg>
                    Buscar no app
                  </button>
                  <input type="text" placeholder="Nome da pessoa" value={nomeExterno}
                    onChange={e => setNomeExterno(e.target.value)}
                    className="w-full px-4 py-2.5 text-[13px] bg-paper border border-line rounded-[11px] outline-none text-ink-900 mb-2" autoFocus />
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[13px] text-ink-400 font-semibold select-none">+55</span>
                    <input type="tel" placeholder="WhatsApp (opcional)" value={wppExterno}
                      onChange={e => setWppExterno(e.target.value.replace(/\D/g, '').slice(0, 11))}
                      className="w-full pl-14 pr-4 py-2.5 text-[13px] bg-paper border border-line rounded-[11px] outline-none text-ink-900" />
                  </div>
                  {wppExterno.length >= 10 && (
                    <p className="text-[11px] text-ink-400 mt-2 leading-relaxed">Ao confirmar, abriremos o WhatsApp com um link de convite para o app.</p>
                  )}
                </div>
              ) : (
                /* Modo busca */
                <div className="mb-4">
                  <div className="relative">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9A93A4" strokeWidth="2.2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                    <input type="text" placeholder="Buscar pelo nome ou @usuário..." value={buscaRegistrar}
                      onChange={e => setBuscaRegistrar(e.target.value)}
                      className="w-full pl-9 pr-3 py-2.5 text-[13px] bg-paper border border-line rounded-[11px] outline-none text-ink-900" autoFocus />
                  </div>
                  {buscandoRegistrar && <p className="text-[12px] text-ink-400 text-center py-3">Buscando...</p>}
                  {usuariosBuscados.length > 0 && (
                    <div className="mt-1 rounded-[11px] border border-line overflow-hidden">
                      {usuariosBuscados.map((u, idx) => (
                        <div key={u.id} onClick={() => { setLeitorRegistrar(u); setUsuariosBuscados([]); setBuscaRegistrar(''); }}
                          className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-paper-2 transition-colors"
                          style={{borderTop: idx > 0 ? '1px solid #F2EEE9' : 'none'}}>
                          <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0 text-[12px] font-bold text-white"
                            style={{background:'linear-gradient(150deg,#6E50F0,#3F27B0)'}}>
                            {u.foto_url
                              ? <img src={u.foto_url} alt="" className="w-full h-full object-cover" />
                              : (u.nome || 'U').charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="text-[13px] font-semibold text-ink-900">{u.nome}</p>
                            {u.username && <p className="text-[11px] text-ink-400">@{u.username}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {buscaRegistrar.length >= 2 && !buscandoRegistrar && usuariosBuscados.length === 0 && (
                    <p className="text-[12px] text-ink-400 text-center py-3">Nenhum usuario encontrado</p>
                  )}
                  <button onClick={() => { setExpandirExterno(true); setBuscaRegistrar(''); setUsuariosBuscados([]); }}
                    className="w-full mt-3 flex items-center gap-2 px-3 py-2.5 rounded-[11px] text-[13px] font-semibold text-ink-400 bg-transparent border-0 cursor-pointer hover:bg-paper-2 transition-colors text-left"
                    style={{border:'1.5px dashed #D4CFE2'}}>
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                    Nao esta no app? Adicionar pelo WhatsApp
                  </button>
                </div>
              )}

              {/* Prazo */}
              <div className="mb-5">
                <p className="text-[12px] font-semibold text-ink-400 mb-2">Prazo do empréstimo</p>
                <div className="flex gap-2 flex-wrap">
                  {[7, 14, 21, 30, 0].map(d => (
                    <button key={d} onClick={() => setPrazoRegistrar(d)}
                      className="flex-1 py-2 rounded-[10px] text-[13px] font-semibold border cursor-pointer"
                      style={{ background: prazoRegistrar === d ? '#5B3CE4' : 'transparent', color: prazoRegistrar === d ? '#fff' : '#6E6878', borderColor: prazoRegistrar === d ? '#5B3CE4' : '#D4CFE2' }}>
                      {d === 0 ? 'Indeterm.' : `${d}d`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Confirmar */}
              <button onClick={handleRegistrarEmprestimo}
                disabled={registrando || (!leitorRegistrar && !nomeExterno.trim())}
                className="w-full py-[13px] rounded-[14px] text-[14px] font-extrabold border-0 cursor-pointer text-white disabled:opacity-40"
                style={{background:'linear-gradient(150deg,#6E50F0,#3F27B0)'}}>
                {registrando ? 'Registrando...' : 'Confirmar empréstimo'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default Acervo;
